import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatPct, formatDate, formatNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Search, FileSpreadsheet } from "lucide-react";
import ImportDialog from "@/components/ImportDialog";

export const statusBadge = (status) => {
  const map = {
    ABERTO: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    EM_QUEIMA: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    LIQUIDADO: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
  const labels = { ABERTO: "Aberto", EM_QUEIMA: "Em queima", LIQUIDADO: "Liquidado" };
  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${map[status] || ""}`}>{labels[status] || status}</span>;
};

function NewLotDialog({ suppliers, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ code: "", entry_date: today, supplier_id: "", notes: "", is_initial_balance: false });

  const submit = async () => {
    if (!form.code.trim()) { toast.error("Informe o código do lote"); return; }
    setSaving(true);
    try {
      await api.post("/lots", { ...form, supplier_id: form.supplier_id || null });
      toast.success("Lote criado");
      setOpen(false);
      setForm({ code: "", entry_date: today, supplier_id: "", notes: "", is_initial_balance: false });
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="lotes-create-button"><Plus className="h-4 w-4 mr-1" /> Novo lote</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo lote</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Código *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="LOTE-001" data-testid="lot-code-input" />
            </div>
            <div className="space-y-2">
              <Label>Data de entrada</Label>
              <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="lot-date-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fornecedor</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="lot-supplier-select"><SelectValue placeholder="Selecione (opcional)" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.is_initial_balance} onCheckedChange={(v) => setForm({ ...form, is_initial_balance: !!v })} data-testid="lot-initial-balance" />
            Lote de saldo inicial / migração
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="lot-save-button">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar lote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSupplierDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "" });
  const submit = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/suppliers", form);
      toast.success("Fornecedor criado");
      setOpen(false);
      setForm({ name: "", contact: "" });
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="supplier-create-button"><Plus className="h-4 w-4 mr-1" /> Fornecedor</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo fornecedor</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
          <div className="space-y-2"><Label>Contato</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="supplier-save-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Lots() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [lots, setLots] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/lots"), api.get("/suppliers")]);
      setLots(l.data);
      setSuppliers(s.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const filtered = lots.filter((l) => {
    if (statusFilter !== "ALL" && l.status !== statusFilter) return false;
    if (q && !`${l.code} ${l.supplier_name || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Gestão de Lotes</h1>
          <p className="text-muted-foreground mt-1">Unidades gerenciais de investimento</p>
        </div>
        <div className="flex gap-2">
          <ImportDialog entity="lots" onDone={load} trigger={<Button variant="outline" data-testid="import-lots-open"><FileSpreadsheet className="h-4 w-4 mr-2" /> Importar</Button>} />
          <NewSupplierDialog onCreated={load} />
          <NewLotDialog suppliers={suppliers} onCreated={load} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar por código ou fornecedor" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lotes-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="lotes-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os status</SelectItem>
            <SelectItem value="ABERTO">Aberto</SelectItem>
            <SelectItem value="EM_QUEIMA">Em queima</SelectItem>
            <SelectItem value="LIQUIDADO">Liquidado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground">Nenhum lote encontrado. Crie o primeiro lote.</p>
        ) : (
          <Table data-testid="lotes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Entrada</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">CTL</TableHead>
                <TableHead className="text-right">RPA</TableHead>
                <TableHead className="text-right">Margem %</TableHead>
                <TableHead className="text-right">Sell-through</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} className="cursor-pointer" onClick={() => navigate(`/lotes/${l.id}`)} data-testid={`lote-row-${l.code}`}>
                  <TableCell className="font-num font-semibold">{l.code}</TableCell>
                  <TableCell>{l.supplier_name || "—"}</TableCell>
                  <TableCell>{formatDate(l.entry_date)}</TableCell>
                  <TableCell>{statusBadge(l.status)}</TableCell>
                  <TableCell className="text-right font-num">{formatBRL(l.indicators.ctl)}</TableCell>
                  <TableCell className="text-right font-num">{formatBRL(l.indicators.rpa)}</TableCell>
                  <TableCell className={`text-right font-num ${l.indicators.mgp_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatPct(l.indicators.mgp_pct)}</TableCell>
                  <TableCell className="text-right font-num">{formatPct(l.indicators.sell_through)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
